import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import {
  makeStyles,
  TableContainer,
  Table,
  Typography,
  Grid,
  TableHead,
  TableBody,
  Checkbox,
  Box,
  Collapse,
  IconButton,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";

import { PowerAssetIcon } from "../../PowerAssetIcon";
import { Button } from "../../../../components/base/Button";
import { currencyFormatter, nKilowattFormatter, capitalize } from "../../../../services/utils";
import { StyledTableRow, StyledTableCell } from "../../../../components/base/Table";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { ReactComponent as CheckedIcon } from "../../../../assets/images/icons/checked.svg";
import { ReactComponent as UncheckedIcon } from "../../../../assets/images/icons/unchecked.svg";
import { COLORS } from "../../../../theme/colors";
import { useComputeRecommendedPowerAssetsTotal } from "./useComputeRecommendedPowerAssetsTotal";

const useStyles = makeStyles(() => ({
  caption: {
    fontWeight: 700,
  },
  investment: {
    backgroundColor: "#E7FCDC",
  },
  tableContainer: {
    borderRadius: 8,
    maxHeight: 556,
    border: `1px solid ${COLORS.darks[0][10]}`,
    boxShadow: "none",
  },
  bold: {
    fontWeight: "bold",
  },
  checkboxCell: {
    padding: 16,
    width: 18,
  },
}));

export const NewPowerAssetsTable = ({
  data,
  name,
  type,
  handleChangeResults,
  showActionButtons,
  total,
  setOpen,
  id,
}) => {
  const classes = useStyles();
  const [checked, setChecked] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const [totalCapacity, totalTraded, totalExpectedNewPowerRevenues, totalInvestment] =
    useComputeRecommendedPowerAssetsTotal(data);

  useEffect(() => {
    const filteredData = data.filter((item) => item.checked);
    if (data.length === filteredData.length) {
      setChecked(!!filteredData.length);
    } else if (data.length > filteredData.length) {
      setChecked(false);
    }
  }, [data]);

  const handleChangeCheckbox = (i) => {
    const results = data.slice();
    results[i].checked = !results[i].checked;
    handleChangeResults((prev) => ({
      ...prev,
      results,
    }));
  };

  const handleChangeCheckboxes = (event) => {
    const results = data.map((item) => ({
      ...item,
      checked: event.target.checked,
    }));
    handleChangeResults((prev) => ({
      ...prev,
      results,
    }));
    setChecked(event.target.checked);
  };

  return (
    <div style={{ marginBottom: 30 }} id={id}>
      <Grid
        container
        alignItems="center"
        style={{ marginBottom: 20 }}
        justifyContent="space-between"
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PowerAssetIcon type={type} />
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            {name}({data.length})
          </Typography>
        </Box>
        {showActionButtons && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body1">{total} new assets selected</Typography>
            <Box sx={{ margin: "0 40px 0 10px" }}>
              <Button
                onClick={() => setOpen(true)}
                text="approve selected assets"
                disabled={!total}
              />
            </Box>
            <ActionButtonsGroup hideIntegrateBtn />
          </Box>
        )}
      </Grid>
      <TableContainer className={classes.tableContainer}>
        <Collapse in={!collapsed} timeout="auto" collapsedSize={119}>
          <Table aria-label="simple table">
            <TableHead>
              <StyledTableRow style={{ borderBottom: `1px solid ${COLORS.darks[0][10]}` }}>
                <StyledTableCell colSpan={2}>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Location
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Sys. Name
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Power Capacity kWh
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Traded kWh/year
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Expected New Power Revenues
                  </Typography>
                </StyledTableCell>
                <StyledTableCell className={classes.investment}>
                  <Typography variant="subtitle2" className={classes.caption}>
                    Your investment
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              <StyledTableRow style={{ backgroundColor: COLORS.white }}>
                <StyledTableCell className={classes.checkboxCell}>
                  <Checkbox
                    checked={checked}
                    onChange={handleChangeCheckboxes}
                    style={{ color: COLORS.purple }}
                    icon={<UncheckedIcon />}
                    checkedIcon={<CheckedIcon />}
                  />
                </StyledTableCell>
                <StyledTableCell style={{ paddingLeft: 0 }}>
                  <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
                    {collapsed ? <KeyboardArrowRight /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                  <Typography variant="h5" component="span" className={classes.bold}>
                    {data.length} Buildings
                  </Typography>
                </StyledTableCell>
                <StyledTableCell></StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">{nKilowattFormatter(totalCapacity)}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">{nKilowattFormatter(totalTraded)}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">
                    {currencyFormatter(totalExpectedNewPowerRevenues, currencySymbol)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell className={classes.investment}>
                  {currencyFormatter(totalInvestment, currencySymbol)}
                </StyledTableCell>
              </StyledTableRow>
              {data.map((item, i) => (
                <StyledTableRow key={i} style={{ backgroundColor: COLORS.white }}>
                  <StyledTableCell className={classes.checkboxCell}>
                    <Checkbox
                      checked={item.checked}
                      onChange={() => handleChangeCheckbox(i)}
                      style={{ color: COLORS.purple }}
                      icon={<UncheckedIcon />}
                      checkedIcon={<CheckedIcon />}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">{item.location}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">{capitalize(item.type)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">{nKilowattFormatter(item.production)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">{nKilowattFormatter(item.acc_revenue)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">
                      {currencyFormatter(item.expected_power_revenue, currencySymbol)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell className={classes.investment}>
                    {currencyFormatter(0, currencySymbol)}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </TableContainer>
    </div>
  );
};

NewPowerAssetsTable.defaultProps = {
  showActionButtons: false,
  total: 0,
};
